/* eslint-disable react/prop-types */
import React, { useState, FC, HTMLAttributes, SyntheticEvent } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-scroll'
import { useRecoilValue, useRecoilState } from 'recoil'
import { Text, IconButton, Box, Flex, Heading, Skeleton } from '@chakra-ui/core'
import { QuantityBox } from '@engyalo/storefront-components'
import { Hook, IStorefrontSettings } from '@engyalo/storefront-types'
import { categorySelected, searchStarted } from '@engyalo/storefront-events'
import { isIOS, isMobileOnly } from 'react-device-detect'
import {
  IProductPricedPerCollectionLevel1,
  IProductsPricedPerCollectionCollection,
} from '@engyalo/storefront-types/lib/graphql/product'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import { ProductList, ProductListFrom } from '../../components/ProductList'
import { Searchbar } from '../../components/Searchbar'
import { CartIcon } from '../../components/CartIcon'
import { MinAmountProgress } from '../../components/MinAmountProgress'
import { cartStatsState } from '../../recoil/selectors'
import { configState, orderConfigState, searchStats } from '../../recoil/atoms'
import { ISearchStats, IConfig } from '@engyalo/storefront-types/lib/plugins/model'
import { BoxLinkButton } from '../../lib/types'
import { notEmpty } from '../../lib/utilities'
import { skipIfInPreviewMode } from '../../utils/mode'
import Plugin, { usePlugin } from '../../Plugin'

const DEFAULT_LOGO_URL = 'https://storage.googleapis.com/yalo-img/4k/logo-purple.png'

interface IProps extends HTMLAttributes<HTMLDivElement> {
  loading: boolean
  products: (IProductsPricedPerCollectionCollection | null)[]
}

export const Catalogue: FC<IProps> = ({ loading, products }: IProps) => {
  const { t } = useTranslation()
  const { customer, order } = useParams()
  const [searchbarStatus, setSearchbarStatus] = useState(false)
  const [activeSection, setActiveSection] = useState('')
  const cartStats = useRecoilValue(cartStatsState)
  const config = useRecoilValue<IConfig>(configState)
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const [currentSearchStats, setSearchStats] = useRecoilState<ISearchStats>(searchStats)
  const { totalNum, totalPrice } = cartStats
  const storefrontPlugin = usePlugin(Hook.STOREFRONT)
  const hidePrices = (storefrontPlugin?.props as IStorefrontSettings)?.hidePrices ?? false
  const [verticalHeight, setVerticalHeight] = useState(window.innerHeight * 0.01)

  const trackSelectedCategory = (category: string) => (): void => {
    skipIfInPreviewMode(() => {
      categorySelected({
        category,
        session: {
          userID: orderConfig.userID || '',
          customer: config.customer,
        },
      })
    })
  }

  const handleSetActive = (section: string): void => {
    setActiveSection(section)
    const activeSectionRef = document.getElementById(`${section}-link`)
    if (activeSectionRef) {
      activeSectionRef.scrollIntoView({
        behavior: 'auto',
        block: 'center',
        inline: 'center',
      })
    }
  }

  const addDefaultSrc = (e: SyntheticEvent<HTMLImageElement, Event>): void => {
    e.currentTarget.src = DEFAULT_LOGO_URL
  }

  const handleSetInactive = (section: string): void => {
    if (activeSection === section) setActiveSection('')
  }

  const getMarginBottomCatalogList = ():
    | string
    | number
    | symbol
    | (string | number | symbol | null)[]
    | { [x: string]: string | number | symbol | undefined; [x: number]: string | number | symbol | undefined }
    | null
    | undefined => {
    if (totalNum > 0) {
      if (isIOS && isMobileOnly) {
        return '11.4rem'
      }
      return '0.8rem'
    }
    return 0
  }

  return (
    <Flex h="100%" w="100%" flexDirection="column">
      {searchbarStatus && <Searchbar status={searchbarStatus} closeAction={(): void => setSearchbarStatus(false)} />}
      <Flex
        w="100%"
        maxW="42.5rem"
        justifyContent="space-between"
        padding="0.8rem 0.8rem 0 0.8rem"
        position="sticky"
        top={0}
        flex="0 0 auto"
        zIndex={2}
        background="white"
        flexDir="column"
      >
        {!searchbarStatus && (
          <Flex
            h="40px"
            w="100%"
            maxW="42.5rem"
            justifyContent="space-between"
            padding="0.8rem 0.8rem 0 0.8rem"
            flex="0 0 auto"
          >
            {loading ? (
              <Skeleton m=".8rem" height="1.6rem" width="1.6rem" />
            ) : (
              <img onError={addDefaultSrc} alt="logo" height="100%" src={config.logo} />
            )}
            <Flex>
              <IconButton
                h="100%"
                w="3.2rem"
                background="transparent"
                aria-label="search"
                size="lg"
                icon="search"
                onClick={(): void => {
                  const newSearchOpenCount = currentSearchStats.openCount + 1
                  skipIfInPreviewMode(() => {
                    searchStarted({
                      stats: {
                        totalSearchStart: newSearchOpenCount,
                      },
                      session: {
                        userID: orderConfig.userID || '',
                        customer: config.customer,
                      },
                    })
                  })
                  setSearchStats({
                    ...currentSearchStats,
                    openCount: newSearchOpenCount,
                  })
                  setSearchbarStatus(true)
                }}
                borderStyle="none"
                borderColor="unset"
              />
              <CartIcon />
            </Flex>
          </Flex>
        )}
        <Flex width="100%" overflowX="auto" aria-hidden id="navbar" flex="0 0 auto" zIndex={2} maxW="42.5rem">
          {loading
            ? [1, 2, 3, 4].map((number) => <Skeleton key={number} m=".8rem" height="1.6rem" width="10.8rem" />)
            : products.map((collection) => {
                const slug = collection?.name || ''
                if (collection) {
                  return (
                    <Link
                      activeClass="active"
                      onSetActive={handleSetActive}
                      to={slug}
                      spy={true}
                      smooth={true}
                      duration={250}
                      isDynamic={true}
                      containerId="containerElement"
                      onSetInactive={(): void => handleSetInactive(slug)}
                      id={`${slug}-link`}
                      key={`${slug}-link`}
                      onClick={trackSelectedCategory(slug)}
                    >
                      <Box
                        paddingY="1.6rem"
                        paddingX="4rem"
                        fontSize="categoryTab"
                        borderBottom="0.2rem solid"
                        borderColor={activeSection === slug ? 'primary.500' : 'transparent'}
                        cursor="pointer"
                        whiteSpace="nowrap"
                      >
                        {collection.name}
                      </Box>
                    </Link>
                  )
                }

                return null
              })}
        </Flex>
      </Flex>
      {searchbarStatus && (
        <Box
          position="absolute"
          opacity={0.5}
          backgroundColor="black"
          width={['100%', '425px']}
          height={['100%', '720px']}
          zIndex={50}
        />
      )}
      <Box flex="1" id="containerElement" paddingBottom="2rem" overflowY="scroll">
        {loading
          ? [1, 2, 3].map((number) => (
              <Box key={number}>
                <Skeleton height="1.8rem" width="6rem" margin="1.2rem" />

                <ProductList products={Array(5).fill({})} from={ProductListFrom.COLLECTIONS} isLoading={loading} />
              </Box>
            ))
          : products.map((collection) => {
              if (collection && collection.productsPriced && collection.name) {
                const notEmptyProducts: IProductPricedPerCollectionLevel1[] = collection.productsPriced.filter(notEmpty)
                const productsPriced = hidePrices
                  ? notEmptyProducts.map((product: IProductPricedPerCollectionLevel1) => ({
                      ...product,
                      price: 0,
                    }))
                  : notEmptyProducts

                const showPrice = collection.enabledPrice === null ? true : collection.enabledPrice

                return (
                  <div id={collection.name} key={collection.name}>
                    <Heading as="h3" fontSize="category" padding="0.8rem 0 0.4rem 1.2rem" fontWeight="500">
                      {collection.name}
                    </Heading>
                    <ProductList products={productsPriced} from={ProductListFrom.COLLECTIONS} showPrice={showPrice} />
                  </div>
                )
              }

              return null
            })}
      </Box>
      {totalNum > 0 && (
        <Box
          zIndex={1}
          paddingY="1.2rem"
          paddingX="0.8rem"
          boxShadow="md"
          backgroundColor="white"
          position="sticky"
          flex="0 0 auto"
          width="100%"
          maxW="42.5rem"
          bottom={0}
        >
          {config.minAmount ? (
            <MinAmountProgress
              currencyPrefix={config.currencyPrefix}
              currentAmount={Number(totalPrice)}
              minAmount={config.minAmount}
            />
          ) : null}
          <BoxLinkButton
            to={`/${customer}/${order}/cart`}
            display="flex"
            width="100%"
            paddingY="1.2rem"
            paddingX="1.6rem"
            backgroundColor="primary.100"
            borderColor="transparent"
            fontWeight="500"
            color="white"
            fontFamily="body"
            fontSize="1.1rem"
            borderRadius="0.4rem"
            textDecor="none"
          >
            <QuantityBox size="big" value={totalNum} light />
            <Text flex="1" fontSize="lg" alignSelf="center" textAlign="center">
              {t('catalogue-goToCart')}
            </Text>
            {!hidePrices && <Text fontSize="lg" alignSelf="center">{`${config.currencyPrefix} ${totalPrice}`}</Text>}
          </BoxLinkButton>
        </Box>
      )}
    </Flex>
  )
}
